import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn, ui } from '../../utils';
import { useFocusTrap } from '../../hooks/useFocusTrap';

// D2-12: branded replacement for `window.confirm`. Native confirm blocks the
// JS thread, ignores the app theme, and looks broken inside the PWA shell.
// Usage:
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete conversation?', destructive: true }))) return;
// The promise resolves `true` on confirm and `false` on cancel / Esc /
// backdrop click — the same contract as `window.confirm`, minus the jank.

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the danger style (red). */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

interface ConfirmDialogProps {
  options: ConfirmOptions;
  onResolve: (confirmed: boolean) => void;
}

export function ConfirmDialog({ options, onResolve }: ConfirmDialogProps) {
  const cancel = useCallback(() => onResolve(false), [onResolve]);
  const panelRef = useFocusTrap<HTMLDivElement>(true, cancel);

  const {
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
  } = options;

  return (
    <div
      className={ui.modalBackdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cancel();
        }
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={message ? 'confirm-dialog-message' : undefined}
        className={cn(ui.modalPanel, 'max-w-md')}
      >
        <div className="flex items-start gap-4 px-6 pt-6">
          <div
            aria-hidden="true"
            className={cn(
              'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border',
              destructive
                ? 'border-red-500/25 bg-red-500/10 text-red-300'
                : 'border-blue-500/25 bg-blue-500/10 text-blue-300',
            )}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0 pt-1">
            <h2 id="confirm-dialog-title" className="text-base font-semibold text-foreground-bright">
              {title}
            </h2>
            {message ? (
              <p id="confirm-dialog-message" className="mt-2 break-words text-sm leading-6 text-muted-foreground">
                {message}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-5">
          <button
            type="button"
            onClick={cancel}
            className={cn(ui.button.base, ui.button.secondary)}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onResolve(true)}
            className={cn(ui.button.base, destructive ? ui.button.danger : ui.button.primary)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    // A second confirm while one is open cancels the first — same as the
    // browser, which can only show one native dialog at a time.
    pendingRef.current?.resolve(false);

    return new Promise<boolean>((resolve) => {
      const next: PendingConfirm = { options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const handleResolve = useCallback((confirmed: boolean) => {
    pendingRef.current?.resolve(confirmed);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending ? <ConfirmDialog options={pending.options} onResolve={handleResolve} /> : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return confirm;
}
